/**
 * The app's shared state, in one place every layout and view reads from.
 *
 * Model: the library (engine data), the player (core/player.ts), the registry
 * (core/registry.ts). View: layouts and views, which only read this context and
 * call its actions. Controller: the actions here and in the registry's
 * contributions. Components do not talk to each other directly; they change
 * state here, or say something on the bus.
 *
 * The library refreshes when the engine says something changed (engine events),
 * not on timers.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { engineBase, useLibrary } from "../api";
import { readNotation, storeNotation, type KeyNotation } from "../format/notation";
import { parse, type Query } from "../search/parse";
import { EMPTY_FILTERS, filterClauses, type Filters } from "../search/filters";
import { useTheme } from "../theme";
import { bus } from "./bus";
import { startEngineEvents } from "./engineEvents";
import { syncPlugins } from "./plugins";
import { lockedLayout, useLockedLayout } from "./layoutLock";
import { usePref } from "./prefs";

export type LayoutId = string; // "pro" | "compact" | "radio" | a plugin's layout

export interface Nav {
  view: string;
  params: Record<string, string>;
}

function useAppState() {
  const library = useLibrary();
  const theme = useTheme();
  const [chosenLayout, setChosenLayout] = usePref<LayoutId>("nova.layout", "pro");
  // A plugin may lock the layout for this window (core/layoutLock.ts).
  const locked = useLockedLayout();
  const layout = locked ?? chosenLayout;
  const setLayout = useCallback((next: LayoutId) => {
    if (!lockedLayout()) setChosenLayout(next);
  }, [setChosenLayout]);
  const [nav, setNavState] = usePref<Nav>("nova.nav", { view: "songs", params: {} });
  const [notation, setNotationState] = useState<KeyNotation>(readNotation);
  const [sidebar, setSidebar] = usePref("nova.sidebar.open", true);
  const [inspector, setInspector] = usePref("nova.inspector.open", true);
  const [settingsPage, setSettingsPage] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);

  // ---------------------------------------------------------------- search
  const [input, setInput] = useState("");
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const typedQuery: Query = useMemo(() => parse(input), [input]);
  const query = useMemo(() => {
    const extra = filterClauses(filters);
    return extra.length ? { ...typedQuery, clauses: [...typedQuery.clauses, ...extra], isEmpty: false } : typedQuery;
  }, [typedQuery, filters]);
  const offline = library.state !== "connected";

  // The engine filters the whole library; debounced so typing is one request per pause.
  const setQueryRef = useRef(library.setQuery);
  setQueryRef.current = library.setQuery;
  useEffect(() => {
    if (offline) return;
    const timer = window.setTimeout(() => {
      void setQueryRef.current(query.clauses.length || query.free.length ? { clauses: query.clauses, free: query.free } : null);
    }, 220);
    return () => window.clearTimeout(timer);
  }, [query, offline]);

  const addTerm = useCallback((term: string) => setInput((q) => (q.includes(term) ? q : `${q} ${term}`.trim())), []);

  // ---------------------------------------------------------------- engine events
  const refreshRef = useRef(library.refresh);
  refreshRef.current = library.refresh;
  const patchRef = useRef(library.patchRows);
  patchRef.current = library.patchRows;
  const playlistsRef = useRef(library.refreshPlaylists);
  playlistsRef.current = library.refreshPlaylists;
  useEffect(() => {
    const stop = startEngineEvents(engineBase);
    let timer: number | null = null;
    // Coalesce bursts (a scan, a library being analysed) into one refresh.
    const soon = () => {
      if (timer !== null) return;
      timer = window.setTimeout(() => {
        timer = null;
        void refreshRef.current();
      }, 800);
    };
    // Analysis progress names the songs it finished: just those rows are re-read,
    // a second or so later, and the whole library once when analysis goes quiet
    // (a sort or filter by BPM may have changed which songs belong on screen).
    // Reloading every loaded row each second is what froze the window during a scan.
    const finished = new Set<string>();
    let rowsTimer: number | null = null;
    let quietTimer: number | null = null;
    const onProgress = (ids: string[]) => {
      ids.forEach((i) => finished.add(i));
      if (rowsTimer === null) {
        rowsTimer = window.setTimeout(() => {
          rowsTimer = null;
          const batch = [...finished];
          finished.clear();
          void patchRef.current(batch);
        }, 1500);
      }
      if (quietTimer !== null) window.clearTimeout(quietTimer);
      quietTimer = window.setTimeout(() => {
        quietTimer = null;
        soon();
      }, 20000);
    };
    const offs = [
      bus.on("library.*", (e) => {
        const d = (e.data ?? {}) as { trackIds?: unknown; finished?: boolean };
        // A scan's progress is many events a second: the sidebar shows it
        // (core/scanProgress.ts); the library is reloaded once, when it ends.
        if (e.topic === "library.scanning") {
          if (d.finished) soon();
        } else if (e.topic === "library.identifying") {
          if (d.finished) soon();
          else if (Array.isArray(d.trackIds)) onProgress(d.trackIds.map(String));
        } else soon();
      }),
      bus.on("analysis.*", (e) => {
        const ids = (e.data as { trackIds?: unknown } | undefined)?.trackIds;
        if (e.topic === "analysis.progress" && Array.isArray(ids)) onProgress(ids.map(String));
        else soon();
      }),
      bus.on("settings.changed", soon),
      // A flag shows in the Songs column and search (needsedit:yes).
      bus.on("needsEdit.changed", soon),
      bus.on("engine.resync", () => {
        soon();
        void playlistsRef.current();
      }),
      bus.on("engine.connected", () => {
        soon();
        void playlistsRef.current();
        void syncPlugins();
      }),
      bus.on("playlists.changed", () => void playlistsRef.current()),
      bus.on("plugins.*", (e) => {
        if (e.source === "engine" && (e.topic === "plugins.loaded" || e.topic === "plugins.unloaded" || e.topic === "plugins.changed")) {
          void syncPlugins();
        }
      }),
    ];
    return () => {
      stop();
      offs.forEach((off) => off());
      if (timer !== null) window.clearTimeout(timer);
      if (rowsTimer !== null) window.clearTimeout(rowsTimer);
      if (quietTimer !== null) window.clearTimeout(quietTimer);
    };
  }, []);

  useEffect(() => {
    if (library.state === "connected") void library.refreshPlaylists();
  }, [library.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------- navigation
  // Where Back goes: the screens visited before this one, this session.
  const trail = useRef<Nav[]>([]);
  const navRef = useRef(nav);
  navRef.current = nav;
  const navigate = useCallback(
    (view: string, params: Record<string, string> = {}) => {
      const from = navRef.current;
      if (from.view !== view || JSON.stringify(from.params) !== JSON.stringify(params)) {
        trail.current = [...trail.current.slice(-30), from];
      }
      setNavState({ view, params });
      bus.emit("ui.navigate", { view, params });
    },
    [setNavState]
  );
  /** Back to the previous screen (Songs when there is none). */
  const back = useCallback(() => {
    const to = trail.current.pop() ?? { view: "songs", params: {} };
    setNavState(to);
    bus.emit("ui.navigate", to);
  }, [setNavState]);

  const setNotation = useCallback((n: KeyNotation) => {
    setNotationState(n);
    storeNotation(n);
  }, []);

  return {
    library,
    offline,
    theme,
    layout,
    setLayout,
    layoutLocked: locked !== null,
    nav,
    navigate,
    back,
    notation,
    setNotation,
    sidebar,
    setSidebar,
    inspector,
    setInspector,
    settingsPage,
    openSettings: (page = "general") => setSettingsPage(page),
    closeSettings: () => setSettingsPage(null),
    focused,
    setFocused,
    input,
    setInput,
    filters,
    setFilters,
    typedQuery,
    query,
    addTerm,
  };
}

export type AppState = ReturnType<typeof useAppState>;

const Ctx = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const value = useAppState();
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppState {
  const value = useContext(Ctx);
  if (!value) throw new Error("useApp outside AppProvider");
  return value;
}
